import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Zap, DollarSign, Package } from "lucide-react";

interface QuickSaleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, price: number, cost: number) => void;
  touchMode?: boolean;
}

const QuickSaleDialog: React.FC<QuickSaleDialogProps> = ({
  isOpen,
  onClose,
  onAdd,
  touchMode = false,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [price, setPrice] = useState<number | "">("");
  const [cost, setCost] = useState<number | "">(0);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && price !== "" && price > 0) {
      onAdd(name.trim(), Number(price), Number(cost) || 0);
      // Reset form
      setName("");
      setPrice("");
      setCost(0);
      onClose();
    }
  };

  const handleClose = () => {
    setName("");
    setPrice("");
    setCost(0);
    onClose();
  };

  const buttonSize = touchMode ? "lg" : "default";
  const inputClass = touchMode ? "text-lg p-4 h-14" : "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            {t("stock.quickSale")}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quick-sale-name" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                {t("stock.quickSaleName")}
              </Label>
              <Input
                id="quick-sale-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("stock.enterProductName")}
                className={inputClass}
                autoFocus
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-sale-price" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                {t("stock.price")} (TND)
              </Label>
              <Input
                id="quick-sale-price"
                type="number"
                step="0.001"
                min="0.001"
                value={price}
                onChange={(e) => setPrice(e.target.value ? parseFloat(e.target.value) : "")}
                placeholder="0.000"
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-sale-cost" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                {t("stock.cost")} (TND) - {t("common.optional")}
              </Label>
              <Input
                id="quick-sale-cost"
                type="number"
                step="0.001"
                min="0"
                value={cost}
                onChange={(e) => setCost(e.target.value ? parseFloat(e.target.value) : "")}
                placeholder="0.000"
                className={inputClass}
              />
              <p className="text-sm text-muted-foreground">
                {t("stock.quickSaleCostHelp")}
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                size={buttonSize}
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                size={buttonSize}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                disabled={!name.trim() || price === "" || price <= 0}
              >
                <Zap className="h-4 w-4 mr-2" />
                {t("stock.addToCart")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickSaleDialog;

